import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiClock,
  FiFileText,
  FiRefreshCw,
  FiCreditCard,
  FiChevronDown,
  FiChevronUp,
  FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import { portalAPI } from '../../services/api';
import { usePortalAuth } from '../../context/PortalAuthContext';

const formatCurrency = (value) => `${Number(value || 0).toLocaleString('fr-FR')} XOF`;
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '-');

const PaymentScheduleRow = ({ payment, onCheckout, checkingOut }) => {
  const isOverdue = payment.status === 'pending' && payment.due_date && new Date(payment.due_date) < new Date();
  const isPaid = payment.status === 'paid';
  const isPartiallyPaid = payment.status === 'partially_paid';
  const paymentState = payment.payment_state || payment.status;

  const getStatusBadge = () => {
    if (isPaid) return <span className="badge badge-success">Payé</span>;
    if (isPartiallyPaid) return <span className="badge badge-info">Partiellement payé</span>;
    if (paymentState === 'advance_pending') return <span className="badge badge-info">Avance en cours</span>;
    if (isOverdue) return <span className="badge badge-danger">En retard</span>;
    return <span className="badge badge-warning">En attente</span>;
  };

  return (
    <tr>
      <td>{formatDate(payment.due_date)}</td>
      <td>
        <div>{formatCurrency(Number(payment.amount || 0) + Number(payment.late_fee || 0))}</div>
        {isPartiallyPaid && payment.paid_amount && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Payé: {formatCurrency(payment.paid_amount)}
          </div>
        )}
      </td>
      <td>{getStatusBadge()}</td>
      <td style={{ textAlign: 'right' }}>
        {!isPaid && (
          <button
            className="btn btn-sm btn-primary"
            onClick={() => onCheckout(payment.id)}
            disabled={checkingOut === payment.id}
          >
            <FiCreditCard />
            {checkingOut === payment.id ? '…' : isPartiallyPaid ? 'Compléter' : 'Payer'}
          </button>
        )}
        {isPaid && <FiCheckCircle color="var(--success-color, #10b981)" />}
      </td>
    </tr>
  );
};

const ContractCard = ({ contract }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [schedule, setSchedule] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [checkingOut, setCheckingOut] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const loadSchedule = useCallback(async (forceReload = false) => {
    if (loaded && !forceReload) return;
    setScheduleLoading(true);
    setScheduleError(null);
    try {
      const res = await portalAPI.billing.getLeaseContractPaymentSchedule(contract.id);
      const data = res.data;
      const items = Array.isArray(data) ? data : (data?.items || data?.rows || []);
      setSchedule(items);
      setLoaded(true);
    } catch (err) {
      const detail = err.response?.data?.details || err.response?.data?.error || err.message || '';
      console.error('[ContractCard] Erreur chargement échéancier:', detail);
      setScheduleError(`Impossible de charger l'échéancier.${detail ? ` (${detail})` : ''}`);
    } finally {
      setScheduleLoading(false);
    }
  }, [contract.id, loaded]);

  const handleToggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next) await loadSchedule();
  };

  const handleRetry = async () => {
    await loadSchedule(true);
  };

  const handleCheckout = async (paymentId) => {
    navigate(`/portal/lease-payments/${paymentId}/checkout`);
  };

  const pendingCount = schedule.filter(p => p.status !== 'paid').length;
  const paidCount = schedule.filter(p => p.status === 'paid').length;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
          <FiFileText />
          <div>
            <div style={{ fontWeight: 600 }}>{contract.property_title || `Contrat ${contract.contract_number}`}</div>
            {contract.property_address && (
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 2 }}>
                {contract.property_address}
              </div>
            )}
          </div>
        </div>
        <span className={`badge badge-${contract.status === 'active' ? 'success' : 'secondary'}`}>
          {contract.status}
        </span>
      </div>

      <div className="card-body">
        <div className="grid-4" style={{ marginBottom: 12 }}>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 2 }}>Loyer mensuel</div>
            <div style={{ fontWeight: 600 }}>{formatCurrency(contract.monthly_rent)}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 2 }}>Frais d'agence</div>
            <div style={{ fontWeight: 600 }}>{formatCurrency(contract.agency_fee || contract.charges || 0)}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 2 }}>N° Contrat</div>
            <div style={{ fontWeight: 600 }}>{contract.contract_number || '-'}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 2 }}>Début</div>
            <div style={{ fontWeight: 600 }}>{formatDate(contract.start_date)}</div>
          </div>
        </div>

        <button
          className="btn btn-secondary"
          style={{ width: '100%', justifyContent: 'center' }}
          onClick={handleToggle}
        >
          {expanded ? <FiChevronUp /> : <FiChevronDown />}
          {expanded ? 'Masquer l\'échéancier' : 'Voir l\'échéancier'}
          {!expanded && pendingCount > 0 && (
            <span className="badge badge-warning" style={{ marginLeft: 8 }}>
              {pendingCount} en attente
            </span>
          )}
        </button>

        {expanded && (
          <div style={{ marginTop: 16 }}>
            {scheduleLoading ? (
              <div className="loading" style={{ padding: 24 }}>
                <div className="spinner" />
              </div>
            ) : scheduleError ? (
              <div style={{ color: 'var(--danger-color, #ef4444)', padding: 12, textAlign: 'center' }}>
                <FiAlertCircle /> {scheduleError}
                <div style={{ marginTop: 8 }}>
                  <button className="btn btn-sm btn-secondary" onClick={handleRetry}>
                    <FiRefreshCw /> Réessayer
                  </button>
                </div>
              </div>
            ) : schedule.length === 0 ? (
              <div className="empty-state" style={{ padding: 24 }}>
                <FiClock size={32} />
                <p>Aucune échéance trouvée pour ce contrat.</p>
                <button className="btn btn-sm btn-secondary" onClick={handleRetry} style={{ marginTop: 8 }}>
                  <FiRefreshCw /> Recharger
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 12, marginBottom: 12, fontSize: 13 }}>
                  <span style={{ color: 'var(--success-color, #10b981)' }}>
                    <FiCheckCircle /> {paidCount} payée{paidCount > 1 ? 's' : ''}
                  </span>
                  <span style={{ color: 'var(--warning-color, #f59e0b)' }}>
                    <FiClock /> {pendingCount} en attente
                  </span>
                </div>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date d'échéance</th>
                        <th>Montant</th>
                        <th>Statut</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {schedule.map(payment => (
                        <PaymentScheduleRow
                          key={payment.id}
                          payment={payment}
                          onCheckout={handleCheckout}
                          checkingOut={checkingOut}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const PortalLeasePaymentsPage = () => {
  const { user } = usePortalAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await portalAPI.billing.listLeaseContracts();
      setContracts(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Impossible de charger vos contrats.');
      setContracts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Mes Contrats de Location</h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: 4 }}>
            Consultez vos contrats et payez vos échéances directement depuis cet espace.
          </p>
        </div>
        <button className="btn btn-secondary" onClick={fetchContracts} disabled={loading}>
          <FiRefreshCw /> Actualiser
        </button>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner" />
        </div>
      ) : error ? (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <FiAlertCircle size={40} color="var(--danger-color, #ef4444)" />
              <h3>{error}</h3>
              <button className="btn btn-primary" onClick={fetchContracts}>
                Réessayer
              </button>
            </div>
          </div>
        </div>
      ) : contracts.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <FiClock size={48} />
              <h3>Aucun contrat de location trouvé</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Vous n'avez pas encore de contrat de location actif.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {contracts.map(contract => (
            <ContractCard key={contract.id} contract={contract} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalLeasePaymentsPage;
